import type { BrandSpecialPrices, PriceId } from '@iheartjane/types';

import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

import {
  brandSpecialHasOneDiscount,
  getFirstBrandSpecial,
} from './brandSpecialDiscounts';

/**
 * This function returns a title for a brand discount
 * It can be passed either a menuProduct or a brandSpecials object, but not both.
 * If the menuProduct does not have a brand discount, then it will return null, and therefore should suffice as an equality check.
 * If you want to extract a generic title for multiple priceIds, pass only the brand special.
 * It will return the discount amount if all the priceIds share a discount id,
 * otherwise it will return the fallback label.
 */

type BrandSpecialTagLabelKey = 'discount_label' | 'title';

export interface GetJaneGoldTitleProps {
  fallbackLabel?: string;
  menuProduct: ProductViewModel | ProductSearchResultViewModel | AlgoliaProduct;
  priceId?: PriceId;
  priorityValueKey?: BrandSpecialTagLabelKey;
}

const fallbackKeys: {
  [Property in BrandSpecialTagLabelKey]: BrandSpecialTagLabelKey;
} = {
  discount_label: 'title',
  title: 'discount_label',
};

export const getJaneGoldTitle = ({
  fallbackLabel = 'Cash Back',
  menuProduct,
  priceId,
  priorityValueKey = 'discount_label',
}: GetJaneGoldTitleProps) => {
  if (!menuProduct) return null;

  const brandSpecialsPrices = menuProduct.brand_special_prices;

  const brandSpecialPriceValues = Object.values(
    menuProduct?.brand_special_prices || {}
  ).filter(Boolean);

  if (
    !menuProduct.brand_special_prices ||
    brandSpecialPriceValues.length === 0 ||
    !menuProduct.has_brand_discount
  )
    return null;

  if (!priceId)
    return getLabelForMultiplePriceIds(
      brandSpecialsPrices as BrandSpecialPrices,
      fallbackLabel,
      priorityValueKey
    );

  const priceIdSpecial = (brandSpecialsPrices as BrandSpecialPrices)[priceId];
  const fallbackKey = fallbackKeys[priorityValueKey];

  return (
    priceIdSpecial?.[priorityValueKey] ||
    priceIdSpecial?.[fallbackKey] ||
    fallbackLabel
  );
};

const getLabelForMultiplePriceIds = (
  brandSpecials: BrandSpecialPrices,
  fallbackLabel: string,
  priorityValueKey: BrandSpecialTagLabelKey
) => {
  const hasOneDiscountId = brandSpecialHasOneDiscount(brandSpecials);

  if (hasOneDiscountId) {
    const firstSpecial = getFirstBrandSpecial(brandSpecials);
    const fallbackKey = fallbackKeys[priorityValueKey];

    return (
      firstSpecial?.[priorityValueKey] ||
      firstSpecial?.[fallbackKey] ||
      fallbackLabel
    );
  }

  return fallbackLabel;
};
